import {
  faChartSimple,
  faCircleExclamation,
  faCode,
  faTable,
} from "@fortawesome/pro-regular-svg-icons";
import { format } from "sql-formatter";

import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { type TabData, Tabs } from "src/base-components/Tabs";
import {
  type ToolCallContentBlock,
  type ToolMessage,
} from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { useMetricsQuery } from "src/flow/analytics/m1/useMetricsQuery";
import { MetricsChart } from "src/flow/analytics/m1/VisualizeMetrics/MetricsChart";
import { MetricsDataTable } from "src/flow/analytics/m1/VisualizeMetrics/MetricsDataTable";
import type { LabelDictionaries } from "src/flow/analytics/m1/VisualizeMetrics/specialDomainScaling";

type VisualizeMetricsArgs = {
  sql?: string;
  chartDefinition?: Record<string, unknown>;
  message?: string;
};

const asChartDefinition = (
  value: unknown,
): Record<string, unknown> | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

// Prefer the backend-processed spec on toolResult.artifact (carries the
// semantic palette and role-driven scales). Fall back to the raw LLM tool-call
// args for pre-artifact agents and for plot mode.
const getArgs = (
  toolCall: ToolCallContentBlock,
  toolResult: ToolMessage | null,
): VisualizeMetricsArgs => {
  const args = toolCall.args as Record<string, unknown>;
  const artifactChart = asChartDefinition(
    toolResult?.artifact?.chart_definition,
  );
  return {
    sql: typeof args.sql === "string" ? args.sql : undefined,
    chartDefinition: artifactChart ?? asChartDefinition(args.chart_definition),
    message: typeof args.message === "string" ? args.message : undefined,
  };
};

const ChartPanel: React.FC<{
  sql: string | undefined;
  chartDefinition: Record<string, unknown> | undefined;
  labelDictionaries: LabelDictionaries;
}> = ({ sql, chartDefinition, labelDictionaries }) => {
  const { data: rows, isLoading, error } = useMetricsQuery(sql);

  if (!sql) {
    return (
      <EmptyState
        description="Provide a SQL query to see results."
        headline="No SQL"
        icon={faCode}
        size="compact"
      />
    );
  }
  if (!chartDefinition) {
    return (
      <EmptyState
        description="Provide a chart definition to render a visualization."
        headline="No chart definition"
        icon={faChartSimple}
        size="compact"
      />
    );
  }
  if (isLoading) {
    return <Skeleton height="h-full" width="w-full" />;
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        description="The query returned no rows."
        headline="No data"
        icon={faChartSimple}
        size="compact"
      />
    );
  }

  return (
    <MetricsChart
      chartDefinition={chartDefinition}
      labelDictionaries={labelDictionaries}
      rows={rows}
    />
  );
};

const formatSql = (sql: string): string => {
  try {
    return format(sql, { language: "postgresql" });
  } catch {
    return sql;
  }
};

const SqlPanel: React.FC<{ sql: string | undefined }> = ({ sql }) => {
  if (!sql) {
    return <div className="font-inter-normal-12px text-gray-400">No SQL</div>;
  }
  return (
    <CodeEditor language="sql" lineWrapping readOnly value={formatSql(sql)} />
  );
};

const TablePanel: React.FC<{ sql: string | undefined }> = ({ sql }) => {
  const { data: rows, isLoading, error } = useMetricsQuery(sql);

  if (!sql) {
    return (
      <EmptyState
        description="Provide a SQL query to see results."
        headline="No SQL"
        icon={faCode}
        size="compact"
      />
    );
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!isLoading && (!rows || rows.length === 0)) {
    return (
      <EmptyState
        description="The query returned no rows."
        headline="No data"
        icon={faTable}
        size="compact"
      />
    );
  }

  return <MetricsDataTable isLoading={isLoading} rows={rows ?? []} />;
};

export const VisualizeMetricsToolMessage: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
  labelDictionaries: LabelDictionaries;
}> = ({ toolCall, toolResult, labelDictionaries }) => {
  const { sql, chartDefinition, message } = getArgs(toolCall, toolResult);
  const description = message?.trim();

  const tabs: TabData[] = [
    {
      key: "chart",
      label: "Chart",
      content: (
        <ChartPanel
          chartDefinition={chartDefinition}
          labelDictionaries={labelDictionaries}
          sql={sql}
        />
      ),
    },
    { key: "sql", label: "SQL", content: <SqlPanel sql={sql} /> },
    { key: "table", label: "Table", content: <TablePanel sql={sql} /> },
  ];

  return (
    <div className="w-full" data-loc="visualize-metrics-tool-message">
      <Tabs
        containerClassName="w-full"
        defaultActiveKey="chart"
        panelClassName="h-80 w-full pt-3"
        tabClassName="px-2"
        tabListClassName="border-b border-gray-200"
        tabs={tabs}
      />
      {description && (
        <ChatTextContent
          content={description}
          dataLoc="visualize-metrics-message"
          delay={0}
          useMarkdownRenderer
        />
      )}
    </div>
  );
};
